import React from 'react'
import { useFormikContext } from 'formik'

import ProfileFormWrapper from 'components/wrappers/ProfileFormWrapper'

import { getCatalogOptions } from 'utils/helpers'
import useAttributes from 'utils/hooks/useAttributes'

import { ProfileFormValues } from '../config'
import { ReactSelect } from 'shared'

import { useAuth } from 'utils/providers/AuthProvider'
import { useProfileForm } from 'utils/providers/ProfileFormProvider'
import ProfileFormStepLoader from '../ProfileFormStepLoader'

const ProfileFormApparelSizes = () => {
  const { values, errors, setValues } = useFormikContext<ProfileFormValues>()
  const { isUserUpdating } = useProfileForm()
  const { isUserFetching } = useAuth()

  const { attributes, isLoading } = useAttributes([
    'pants_size_w',
    'pants_size_m',
    'tops_size',
    'suits_size',
    'shoe_size_new',
    'dress_size',
    'foot_width',
    'pants_type',
    'ear_piercings',
    'ear_piercing_types',
    'body_piercings',
    'height',
    'weight',
    'body_type',
    'bra_band_size',
    'bra_cup_size',
    'shapewear_size',
  ])

  const {
    tops_size,
    pants_size_w,
    pants_size_m,
    suits_size,
    shoe_size_new,
    dress_size,
    foot_width,
    pants_type,
    ear_piercings,
    ear_piercing_types,
    body_piercings,
    height,
    weight,
    body_type,
    bra_band_size,
    bra_cup_size,
    shapewear_size,
  } = attributes

  if (isUserFetching || isUserUpdating) {
    return <ProfileFormStepLoader />
  }

  return (
    <ProfileFormWrapper
      title="Apparel sizing preferences"
      stepDescription="If you are interested in creating apparel and footwear video reviews, tell us about your sizing for all relevant categories. If the question does not apply to you or if you prefer not to answer, please select the N/A or Prefer not to answer option."
    >
      <ReactSelect
        name="tops_sizing"
        options={getCatalogOptions(tops_size?.options)}
        value={values.getValueByAttribute(
          'tops_size',
          'GENERAL',
          values.attributes
        )}
        label="What is your usual size in tops?"
        placeholder="What is your usual size in tops?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = tops_size?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'tops_size',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.tops_size || ''}
      />
      <ReactSelect
        name="pants_size_w"
        options={getCatalogOptions(pants_size_w?.options)}
        value={values.getValueByAttribute(
          'pants_size_w',
          'GENERAL',
          values.attributes
        )}
        label="What is your usual size in bottoms / pants (women's apparel)?"
        placeholder="What is your usual size in bottoms / pants (women's apparel)?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = pants_size_w?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'pants_size_w',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.pants_size_w || ''}
      />
      <ReactSelect
        name="pants_size_m"
        options={getCatalogOptions(pants_size_m?.options)}
        value={values.getValueByAttribute(
          'pants_size_m',
          'GENERAL',
          values.attributes
        )}
        label="What is your usual size in bottoms / pants (men's apparel)?"
        placeholder="What is your usual size in bottoms / pants (men's apparel)?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = pants_size_m?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'pants_size_m',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.pants_size_m || ''}
      />
      <ReactSelect
        name="pants_type"
        options={getCatalogOptions(pants_type?.options)}
        value={values.getValueByAttribute(
          'pants_type',
          'GENERAL',
          values.attributes
        )}
        label="What fit of pants do you typically prefer?"
        placeholder="What fit of pants do you typically prefer?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = pants_type?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'pants_type',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.pants_type || ''}
      />
      <ReactSelect
        name="dress_size"
        options={getCatalogOptions(dress_size?.options)}
        value={values.getValueByAttribute(
          'dress_size',
          'GENERAL',
          values.attributes
        )}
        label="What is your usual US dress size?"
        placeholder="What is your usual US dress size?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = dress_size?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'dress_size',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.dress_size || ''}
      />
      <ReactSelect
        name="suit_size"
        options={getCatalogOptions(suits_size?.options)}
        value={values.getValueByAttribute(
          'suits_size',
          'GENERAL',
          values.attributes
        )}
        label="What is your usual suit size?"
        placeholder="What is your usual suit size?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = suits_size?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'suits_size',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.suits_size || ''}
      />
      <ReactSelect
        name="height"
        options={getCatalogOptions(height?.options)}
        value={values.getValueByAttribute(
          'height',
          'GENERAL',
          values.attributes
        )}
        label="What is your approximate height?"
        placeholder="What is your approximate height?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = height?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'height',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.height || ''}
      />
      <ReactSelect
        name="weight"
        options={getCatalogOptions(weight?.options)}
        value={values.getValueByAttribute(
          'weight',
          'GENERAL',
          values.attributes
        )}
        label="Which of the following is closest to your body weight?"
        placeholder="Which of the following is closest to your body weight?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = weight?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'weight',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.weight || ''}
      />
      <ReactSelect
        name="body_type"
        isMulti
        options={getCatalogOptions(body_type?.options)}
        value={values.getValuesByAttribute(
          'body_type',
          'GENERAL',
          values.attributes
        )}
        label="How would you describe your body type and shape? Please select all that apply!"
        placeholder="How would you describe your body type and shape? Please select all that apply!"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItems = body_type?.options?.filter((item) =>
            e.find((el) => el.value === item.code)
          )
          setValues(
            values.setAttributeValue({
              attribute: 'body_type',
              category: 'GENERAL',
              value: catalogItems || null,
            })
          )
        }}
        error={errors?.validationErrors?.body_type || ''}
      />
      <ReactSelect
        name="shoe_size"
        options={getCatalogOptions(shoe_size_new?.options)}
        value={values.getValueByAttribute(
          'shoe_size_new',
          'GENERAL',
          values.attributes
        )}
        label="What is your usual shoe size?"
        placeholder="What is your usual shoe size?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = shoe_size_new?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'shoe_size_new',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.shoe_size_new || ''}
      />
      <ReactSelect
        name="foot_width"
        options={getCatalogOptions(foot_width?.options)}
        value={values.getValueByAttribute(
          'foot_width',
          'GENERAL',
          values.attributes
        )}
        label="How would you describe the width of your foot?"
        placeholder="How would you describe the width of your foot?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = foot_width?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'foot_width',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.foot_width || ''}
      />
      <ReactSelect
        name="ear_piercings"
        options={getCatalogOptions(ear_piercings?.options)}
        value={values.getValueByAttribute(
          'ear_piercings',
          'GENERAL',
          values.attributes
        )}
        label="Do you have your ears pierced? If so, how many ear piercings do you have?"
        placeholder="Do you have your ears pierced? If so, how many ear piercings do you have?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = ear_piercings?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'ear_piercings',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.ear_piercings || ''}
      />
      <ReactSelect
        name="ear_piercing_types"
        isMulti
        options={getCatalogOptions(ear_piercing_types?.options)}
        value={values.getValuesByAttribute(
          'ear_piercing_types',
          'GENERAL',
          values.attributes
        )}
        label="Do you have any of the following kinds of ear piercings?"
        placeholder="Do you have any of the following kinds of ear piercings?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItems = ear_piercing_types?.options?.filter((item) =>
            e.find((el) => el.value === item.code)
          )
          setValues(
            values.setAttributeValue({
              attribute: 'ear_piercing_types',
              category: 'GENERAL',
              value: catalogItems || null,
            })
          )
        }}
        error={errors?.validationErrors?.ear_piercing_types || ''}
      />
      <ReactSelect
        name="body_piercings"
        isMulti
        options={getCatalogOptions(body_piercings?.options)}
        value={values.getValuesByAttribute(
          'body_piercings',
          'GENERAL',
          values.attributes
        )}
        label="Do you have any of the following body piercings?"
        placeholder="Do you have any of the following body piercings?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItems = body_piercings?.options?.filter((item) =>
            e.find((el) => el.value === item.code)
          )
          setValues(
            values.setAttributeValue({
              attribute: 'body_piercings',
              category: 'GENERAL',
              value: catalogItems || null,
            })
          )
        }}
        error={errors?.validationErrors?.body_piercings || ''}
      />
      <ReactSelect
        name="bra_band_size"
        options={getCatalogOptions(bra_band_size?.options)}
        value={values.getValueByAttribute(
          'bra_band_size',
          'GENERAL',
          values.attributes
        )}
        label="What is your typical bra band size?"
        placeholder="What is your typical bra band size?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = bra_band_size?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'bra_band_size',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.bra_band_size || ''}
      />
      <ReactSelect
        name="bra_cup_size"
        options={getCatalogOptions(bra_cup_size?.options)}
        value={values.getValueByAttribute(
          'bra_cup_size',
          'GENERAL',
          values.attributes
        )}
        label="What is your typical bra cup size?"
        placeholder="What is your typical bra cup size?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = bra_cup_size?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'bra_cup_size',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.bra_cup_size || ''}
      />
      <ReactSelect
        name="shapewear_size"
        options={getCatalogOptions(shapewear_size?.options)}
        value={values.getValueByAttribute(
          'shapewear_size',
          'GENERAL',
          values.attributes
        )}
        label="Do you use shapewear or waist trainers? If so, what size do you typically wear in shapewear?"
        placeholder="Do you use shapewear or waist trainers? If so, what size do you typically wear in shapewear?"
        margin="dense"
        isLoading={isLoading}
        onChange={(e) => {
          const catalogItem = shapewear_size?.options?.find(
            (item) => item.code === e?.value
          )
          setValues(
            values.setAttributeValue({
              attribute: 'shapewear_size',
              category: 'GENERAL',
              value: catalogItem || null,
            })
          )
        }}
        error={errors?.validationErrors?.shapewear_size || ''}
      />
    </ProfileFormWrapper>
  )
}

export default ProfileFormApparelSizes
